import { useTranslation } from '../i18n/useTranslation';
import type { AssetAllocation, PortfolioData } from '../types/gateway';

interface PortfolioPanelProps {
  portfolio: PortfolioData;
  className?: string;
}

type ZScoreColorClass = 'text-success' | 'text-warning' | 'text-danger';

function zScoreColor(z: number): ZScoreColorClass {
  const abs = Math.abs(z);
  if (abs > 2) return 'text-danger';
  if (abs >= 1) return 'text-warning';
  return 'text-success';
}

function formatZScore(z: number): string {
  return (z >= 0 ? '+' : '') + z.toFixed(1);
}

interface AssetRowProps {
  asset: AssetAllocation;
}

function AssetRow({ asset }: AssetRowProps) {
  const weightFraction = parseFloat(asset.weight);
  const widthPct = Math.min(Math.max(weightFraction * 100, 0), 100);
  const zColor = zScoreColor(asset.z_score);

  return (
    <tr className="border-b border-border-subtle last:border-b-0 hover:bg-card-elevated transition-colors">
      {/* Asset name */}
      <td className="px-2 py-1.5 text-[10px] font-mono text-foreground whitespace-nowrap">
        {asset.name}
      </td>

      {/* Weight with bar */}
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1.5">
          <div
            className="h-1 rounded-full bg-primary/20 overflow-hidden"
            style={{ width: '48px' }}
            role="presentation"
          >
            <div
              className="h-full rounded-full bg-primary/60"
              style={{ width: `${widthPct}%` }}
            />
          </div>
          <span className="text-[10px] font-mono text-muted-foreground tabular-nums whitespace-nowrap">
            {asset.weight_label}
          </span>
        </div>
      </td>

      {/* Z-score */}
      <td className={`px-2 py-1.5 text-[10px] font-mono tabular-nums text-right ${zColor}`}>
        {formatZScore(asset.z_score)}
      </td>

      {/* Factor */}
      <td className="px-2 py-1.5 text-[10px] font-mono text-muted-foreground whitespace-nowrap">
        {asset.factor}
      </td>

      {/* Action badge */}
      <td className="px-2 py-1.5">
        <span
          className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider whitespace-nowrap"
          style={{ color: asset.action_color }}
        >
          <span
            className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
            style={{ backgroundColor: asset.action_color }}
            aria-hidden="true"
          />
          {asset.action}
        </span>
      </td>
    </tr>
  );
}

function AssetCard({ asset }: AssetRowProps) {
  const weightFraction = parseFloat(asset.weight);
  const widthPct = Math.min(Math.max(weightFraction * 100, 0), 100);
  const zColor = zScoreColor(asset.z_score);

  return (
    <article
      className="border border-border-subtle rounded-sm bg-background/40 p-2"
      aria-label={asset.name}
      data-testid="portfolio-asset-card-mobile"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-[10px] text-foreground font-bold truncate">
            {asset.name}
          </div>
          <div className="font-mono text-[8px] text-muted-foreground uppercase tracking-wider truncate">
            {asset.factor}
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1 text-[8px] font-mono uppercase tracking-wider whitespace-nowrap shrink-0"
          style={{ color: asset.action_color }}
        >
          <span
            className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
            style={{ backgroundColor: asset.action_color }}
            aria-hidden="true"
          />
          {asset.action}
        </span>
      </div>
      <div className="mt-2 grid grid-cols-[1fr_auto] items-center gap-2">
        <div className="h-1 rounded-full bg-primary/20 overflow-hidden">
          <div
            className="h-full rounded-full bg-primary/60"
            style={{ width: `${widthPct}%` }}
          />
        </div>
        <span className="font-mono text-[9px] text-muted-foreground tabular-nums">
          {asset.weight_label}
        </span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="font-mono text-[8px] uppercase tracking-widest text-muted-foreground">
          z score
        </span>
        <span className={`font-mono text-[10px] tabular-nums font-bold ${zColor}`}>
          {formatZScore(asset.z_score)}
        </span>
      </div>
    </article>
  );
}

export function PortfolioPanel({ portfolio, className = '' }: PortfolioPanelProps) {
  const { t } = useTranslation();
  return (
    <div
      className={`bg-card rounded border border-border flex flex-col ${className}`}
      role="region"
      aria-label="Portfolio panel"
    >
      {/* Panel header */}
      <div className="min-h-6 flex items-center justify-between gap-2 px-2 py-1 border-b border-border-subtle shrink-0">
        <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-widest">
          {t('portfolio.header')}
        </span>
        <span className="text-[9px] font-mono text-muted-foreground text-right">
          {t('portfolio.target_sharpe')}:{' '}
          <span className="text-primary">{portfolio.target_sharpe.toFixed(2)}</span>
        </span>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden p-2 space-y-2 overflow-auto">
        {portfolio.assets.map((asset) => (
          <AssetCard key={asset.name} asset={asset} />
        ))}
      </div>

      {/* Desktop asset table */}
      <div className="hidden md:block flex-1 overflow-auto" data-testid="portfolio-asset-table-desktop">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-border-subtle">
              <th className="px-2 py-1 text-[9px] font-mono text-muted-foreground uppercase tracking-widest text-left">
                Asset
              </th>
              <th className="px-2 py-1 text-[9px] font-mono text-muted-foreground uppercase tracking-widest text-left">
                Weight
              </th>
              <th className="px-2 py-1 text-[9px] font-mono text-muted-foreground uppercase tracking-widest text-right">
                Z
              </th>
              <th className="px-2 py-1 text-[9px] font-mono text-muted-foreground uppercase tracking-widest text-left">
                Factor
              </th>
              <th className="px-2 py-1 text-[9px] font-mono text-muted-foreground uppercase tracking-widest text-left">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {portfolio.assets.map((asset) => (
              <AssetRow key={asset.name} asset={asset} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
